import { useCallback, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";

export interface FormDraftState {
    dirty: boolean;
    submitting: boolean;
    error: string | null;
    discardPromptOpen: boolean;
}

export interface FormDraftActions {
    markChanged: () => void;
    setError: (error: string | null) => void;
    beginSubmission: () => boolean;
    endSubmission: () => void;
    requestClose: () => "close" | "confirm" | "blocked";
    dismissDiscard: () => void;
    reset: () => void;
}

export interface FormDraftContextValue {
    state: FormDraftState;
    actions: FormDraftActions;
    meta: { submittingRef: MutableRefObject<boolean> };
}

export function useFormDraft(): FormDraftContextValue {
    const [dirty, setDirty] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
    const dirtyRef = useRef(false);
    const submittingRef = useRef(false);

    const markChanged = useCallback(() => {
        dirtyRef.current = true;
        setDirty(true);
    }, []);
    const beginSubmission = useCallback(() => {
        if (submittingRef.current) return false;
        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        return true;
    }, []);
    const endSubmission = useCallback(() => {
        submittingRef.current = false;
        setSubmitting(false);
    }, []);
    const requestClose = useCallback((): "close" | "confirm" | "blocked" => {
        if (submittingRef.current) return "blocked";
        if (dirtyRef.current) {
            setDiscardPromptOpen(true);
            return "confirm";
        }
        return "close";
    }, []);
    const dismissDiscard = useCallback(() => setDiscardPromptOpen(false), []);
    const reset = useCallback(() => {
        dirtyRef.current = false;
        submittingRef.current = false;
        setDirty(false);
        setSubmitting(false);
        setError(null);
        setDiscardPromptOpen(false);
    }, []);

    const state = useMemo(() => ({ dirty, submitting, error, discardPromptOpen }), [dirty, submitting, error, discardPromptOpen]);
    const actions = useMemo<FormDraftActions>(() => ({
        markChanged,
        setError,
        beginSubmission,
        endSubmission,
        requestClose,
        dismissDiscard,
        reset,
    }), [markChanged, beginSubmission, endSubmission, requestClose, dismissDiscard, reset]);

    return { state, actions, meta: { submittingRef } };
}
